/**
 * derivePlanNarrative — pure helper that synthesises a one-to-two-
 * sentence plain-English summary of a plan, tuned for the inline
 * Plan Review surface (Spec 23 P10.4).
 *
 * The LoomPlanReview already surfaces numeric stats (module count,
 * retrievals, estimated time). This narrative adds what the numbers
 * don't convey: which section the run will OPEN with, why the
 * planner chose that order, and how the writer's write-strategy
 * choice will feel at runtime. Grounded entirely in ``PlanData``
 * the backend already returns — no fabricated content, no
 * hard-coded doctype strings.
 *
 * SRP: pure string builder. No React, no state. Tests cover every
 * branch so the grammar can't drift silently.
 */

import type { PlanData } from "./components/LoomPlanReview";
import type { WriteStrategy } from "./selection";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** First module with ≥1 other module depending on it (via
 *  shared_refs). Used to anchor the "Starts with §X" opener —
 *  planners typically put the upstream module first because
 *  downstream modules want its output. Returns null when there's no
 *  signal to rank by. */
export function pickLeadModule(plan: PlanData): PlanData["modules"][number] | null {
  if (!plan.modules || plan.modules.length === 0) return null;
  // First pass: count how many other modules reference each module's
  // output. shared_refs is the planner's "this module reads from
  // these shared retrievals" — an honest, if indirect, dep signal.
  const referenceCount = new Map<string, number>();
  for (const module of plan.modules) {
    for (const ref of module.shared_refs ?? []) {
      referenceCount.set(ref, (referenceCount.get(ref) ?? 0) + 1);
    }
  }
  // Pick the module with the most downstream references; break ties
  // by plan order (first one wins).
  let best: PlanData["modules"][number] | null = null;
  let bestCount = -1;
  for (const module of plan.modules) {
    const count = referenceCount.get(module.module_id) ?? 0;
    if (count > bestCount) {
      bestCount = count;
      best = module;
    }
  }
  return best;
}

/** Return the set of module ids that reference ``targetId`` in their
 *  shared_refs. Exported for tests + potential future surfaces that
 *  want to show "§X is read by §Y, §Z". */
export function modulesThatReference(plan: PlanData, targetId: string): string[] {
  const out: string[] = [];
  for (const module of plan.modules ?? []) {
    if ((module.shared_refs ?? []).includes(targetId)) out.push(module.module_id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// PlanHighlights derivation (Spec 23 PR-A)
// ---------------------------------------------------------------------------

export interface PlanHighlightsData {
  /** Total module / step count. */
  stepCount: number;
  /** Total estimated minutes (rounded). */
  totalMin: number;
  /** Modules pulled in via the writer's selection-resolver implication
   *  (parent + dep auto-include). The shell knows this; PlanHighlights
   *  surfaces it as a 'why is this here?' breadcrumb. */
  impliedCount: number;
  /** Modules with ≥ MIN_DOWNSTREAM downstream references — the
   *  'critical' ones whose output many other sections will read. */
  criticalCount: number;
}

/** Threshold for 'critical' classification. A module that ≥1 other
 *  module reads from is meaningful; the design uses 'hero' as a hand-
 *  curated flag, but we don't have hero data from the backend. Using
 *  ≥1 downstream-reference is the most honest proxy. Tunable via this
 *  constant if writer feedback says it's too inclusive. */
export const CRITICAL_DOWNSTREAM_MIN = 1;

/**
 * Build the three big numbers shown in the PlanHighlights card.
 * Pure + total. Returns zeroes for empty / missing plan so callers
 * can render the card without null-guards.
 */
export function derivePlanHighlights(plan: PlanData | null, impliedCount = 0): PlanHighlightsData {
  if (!plan || !plan.modules || plan.modules.length === 0) {
    return { stepCount: 0, totalMin: 0, impliedCount, criticalCount: 0 };
  }
  // Step + minute totals — minutes from summary if present (planner's
  // own estimate), else fall back to summary.estimated_seconds / 60.
  const stepCount = plan.modules.length;
  const seconds = plan.summary?.estimated_seconds ?? 0;
  const totalMin = Math.max(0, Math.round(seconds / 60));

  // Critical = any module that ≥ CRITICAL_DOWNSTREAM_MIN other module
  // references via shared_refs. Same primitive as pickLeadModule but
  // expressed as a count. Pure + deterministic given the plan.
  const referenceCount = new Map<string, number>();
  for (const module of plan.modules) {
    for (const ref of module.shared_refs ?? []) {
      referenceCount.set(ref, (referenceCount.get(ref) ?? 0) + 1);
    }
  }
  let criticalCount = 0;
  for (const module of plan.modules) {
    if ((referenceCount.get(module.module_id) ?? 0) >= CRITICAL_DOWNSTREAM_MIN) {
      criticalCount++;
    }
  }
  return { stepCount, totalMin, impliedCount, criticalCount };
}

/** Format the header subline — "5 steps · ~15m est. · live fill". */
export function formatPlanSubline(stepCount: number, totalMin: number, strategy?: WriteStrategy): string {
  const parts: string[] = [`${stepCount} step${stepCount === 1 ? "" : "s"}`, `~${totalMin}m est.`];
  if (strategy === "live") parts.push("live fill");
  else if (strategy === "batched") parts.push("batched");
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Narrative builder
// ---------------------------------------------------------------------------

/**
 * Build the "Planner says" line. Composition rules:
 *
 *   1. Opener: "Starts with §X" when a lead module is identifiable.
 *      Add " because §Y, §Z read from it" when downstream modules
 *      reference it (limit to 2 for readability). Fall back to "This
 *      plan covers N sections." when there's no dep signal.
 *
 *   2. Strategy line: when provided, append the writer's strategy
 *      preview — "Each section fills the template the moment it
 *      finishes." (live) OR "Gates commit as blocks once each is
 *      complete." (batched).
 *
 *   3. Gap note: when ``plan.summary.gap_count > 0``, append a
 *      one-sentence flag so writers don't miss it.
 *
 * Returns null when the plan has zero modules — no signal to
 * narrate, caller should hide the line entirely.
 */
export function derivePlanNarrative(plan: PlanData | null, strategy?: WriteStrategy): string | null {
  if (!plan || !plan.modules || plan.modules.length === 0) return null;

  const lead = pickLeadModule(plan);
  const parts: string[] = [];

  // Opener.
  if (lead) {
    const downstream = modulesThatReference(plan, lead.module_id);
    if (downstream.length === 0) {
      parts.push(`Starts with §${lead.module_id} · ${lead.title}.`);
    } else {
      const limit = 2;
      const shown = downstream
        .slice(0, limit)
        .map((d) => `§${d}`)
        .join(", ");
      const extra = downstream.length > limit ? ` +${downstream.length - limit} others` : "";
      parts.push(`Starts with §${lead.module_id} because ${shown}${extra} read from it.`);
    }
  } else {
    parts.push(`This plan covers ${plan.modules.length} section${plan.modules.length === 1 ? "" : "s"}.`);
  }

  // Strategy line.
  if (strategy === "live") {
    parts.push("Each section fills the template the moment it finishes.");
  } else if (strategy === "batched") {
    parts.push("Gates commit as blocks once each is complete.");
  }

  // Gap note.
  if (plan.summary && plan.summary.gap_count > 0) {
    parts.push(
      `Note: ${plan.summary.gap_count} gap${plan.summary.gap_count === 1 ? "" : "s"} flagged — review before approving.`,
    );
  }

  return parts.join(" ");
}
